import { COMMUNITIES_ENDPOINTS } from "../config/apiConfig";
import { createAuthAxios, handleApiError } from "../utils/apiUtils";

/**
 * Service for community-related API operations
 */
const communityService = {
  /**
   * Get all communities with search and filtering
   * @param {Object} filters - Filter options
   * @param {string} filters.query - Search term
   * @param {string} filters.type - Community type ('public' | 'private')
   * @param {number} filters.minMembers - Minimum member count
   * @param {number} filters.maxMembers - Maximum member count
   * @param {string} filters.sortBy - Sort field ('createdAt' | 'memberCount' | 'roomCount' | 'name')
   * @param {string} filters.sortOrder - Sort order ('asc' | 'desc')
   * @param {number} filters.page - Page number
   * @param {number} filters.limit - Items per page
   * @returns {Promise<Object>} Communities data with pagination
   */
  getCommunities: async (filters = {}) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.LIST}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get communities for a specific user
   * CORRECT ENDPOINT: GET /api/communities/user/:userId?status=member&limit=100
   * @param {string} userId - User ID
   * @param {string} status - Filter by status: 'all' | 'member' | 'creator' (required)
   * @param {number} limit - Maximum number of communities to return
   * @returns {Promise<Object>} User's communities data
   */
  getUserCommunities: async (userId, status = "all", limit = 50) => {
    try {
      const api = createAuthAxios();
      
      // Build query params - status is always required by the backend
      const params = new URLSearchParams({
        status: status,  // Required parameter
        limit: limit.toString(),
      });

      // Endpoint: /api/communities/user/:userId
      const url = `${COMMUNITIES_ENDPOINTS.LIST}/user/${userId}?${params.toString()}`;
const response = await api.get(url);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },



  /**
   * Create a new community
   * @param {Object} communityData - Community data
   * @param {string} communityData.name - Community name (3-100 chars)
   * @param {string} communityData.description - Community description (10-500 chars)
   * @param {string} communityData.field - Research field
   * @param {string} communityData.subField - Research sub-field
   * @param {string} communityData.type - Community type ('public' | 'private')
   * @param {string} communityData.photoURL - Photo URL or null
   * @param {Object} communityData.settings - Community settings
   * @returns {Promise<Object>} Created community data
   */
  createCommunity: async (communityData) => {
    try {
      const api = createAuthAxios();
      const payload = {
        name: communityData.name,
        description: communityData.description,
        field: communityData.field,
        subField: communityData.subField,
        type: communityData.type || "public",
        photoURL: communityData.photoURL || null,
        settings: {
          allowMemberRoomCreation:
            communityData.allowMemberRoomCreation || false,
          requireApprovalForJoining:
            communityData.requireApprovalForJoining !== false,
          allowPublicViewing: communityData.allowPublicViewing !== false,
        },
      };

      // Debug: Log the payload being sent to backend
const response = await api.post(COMMUNITIES_ENDPOINTS.CREATE, payload);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community details (basic info only)
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Community details
   */
  getCommunityDetails: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(COMMUNITIES_ENDPOINTS.DETAIL(communityId));
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get complete community preview data (analytics, activity, ratings, etc.)
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Complete preview data
   */
  getCommunityPreview: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.PREVIEW(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community analytics
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Analytics data
   */
  getCommunityAnalytics: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.ANALYTICS(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community activity feed
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Activity data
   */
  getCommunityActivity: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.ACTIVITY(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get trending topics
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Trending topics
   */
  getTrendingTopics: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.TRENDING(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community guidelines
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Guidelines data
   */
  getCommunityGuidelines: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.GUIDELINES(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Add community guideline (moderators only)
   * @param {string} communityId - Community ID
   * @param {Object} guideline - Guideline data
   * @returns {Promise<Object>} Response
   */
  addCommunityGuideline: async (communityId, guideline) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.GUIDELINES(communityId),
        guideline
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Update community guidelines (moderators only)
   * @param {string} communityId - Community ID
   * @param {Array} guidelines - Guidelines array
   * @returns {Promise<Object>} Response
   */
  updateCommunityGuidelines: async (communityId, guidelines) => {
    try {
      const api = createAuthAxios();
      const response = await api.put(
        COMMUNITIES_ENDPOINTS.GUIDELINES(communityId),
        { guidelines }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Delete community guideline (moderators only)
   * @param {string} communityId - Community ID
   * @param {string} guidelineId - Guideline ID
   * @returns {Promise<Object>} Response
   */
  deleteCommunityGuideline: async (communityId, guidelineId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        `${COMMUNITIES_ENDPOINTS.GUIDELINES(communityId)}/${guidelineId}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Submit community rating
   * @param {string} communityId - Community ID
   * @param {Object} ratingData - Rating data
   * @returns {Promise<Object>} Response
   */
  submitCommunityRating: async (communityId, ratingData) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.RATING(communityId),
        ratingData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community rating summary
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Rating summary
   */
  getCommunityRatingSummary: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.RATING_SUMMARY(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get user's rating for community
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} User's rating
   */
  getMyCommunityRating: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.MY_RATING(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Delete user's community rating
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Response
   */
  deleteMyCommunityRating: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.RATING(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get all community ratings (moderators only)
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} All ratings data
   */
  getAllCommunityRatings: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.ALL_RATINGS(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Update community (Creator only)
   * @param {string} communityId - Community ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated community data
   */
  updateCommunity: async (communityId, updateData) => {
    try {
      const api = createAuthAxios();
      const response = await api.put(
        COMMUNITIES_ENDPOINTS.UPDATE(communityId),
        updateData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Update community settings (Creator only)
   * @param {string} communityId - Community ID
   * @param {Object} settingsData - Settings data
   * @returns {Promise<Object>} Updated settings response
   */
  updateCommunitySettings: async (communityId, settingsData) => {
    try {
      const api = createAuthAxios();
      const response = await api.patch(
        `${COMMUNITIES_ENDPOINTS.DETAIL(communityId)}/settings`,
        settingsData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Direct join community (for public communities with allowDirectJoin enabled)
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Join response
   */
  directJoinCommunity: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        `${COMMUNITIES_ENDPOINTS.DETAIL(communityId)}/join-direct`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Delete community (Creator only)
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteCommunity: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.DELETE(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Request to join community
   * @param {string} communityId - Community ID
   * @param {string} message - Optional join message
   * @returns {Promise<Object>} Join request response
   */
  requestToJoinCommunity: async (communityId, message = "") => {
    try {
      const api = createAuthAxios();
      const response = await api.post(COMMUNITIES_ENDPOINTS.JOIN(communityId), {
        message: message.trim(),
      });
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Leave community
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Leave confirmation
   */
  leaveCommunity: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(COMMUNITIES_ENDPOINTS.LEAVE(communityId));
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community members
   * @param {string} communityId - Community ID
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Members data with pagination
   */
  getCommunityMembers: async (communityId, filters = {}) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.MEMBERS(communityId)}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Upload community photo (3-step process)
   * @param {string} communityId - Community ID
   * @param {File} file - Image file
   * @returns {Promise<Object>} Upload response
   */
  uploadCommunityPhoto: async (communityId, file) => {
    try {
      const api = createAuthAxios();

      // Validate file
      if (!file) throw new Error("No file selected");
      if (file.size > 5 * 1024 * 1024)
        throw new Error("File size must be under 5MB");
      if (!file.type.startsWith("image/"))
        throw new Error("Only image files are allowed");

      // Step 1: Get upload URL
      const uploadUrlResponse = await api.post(
        COMMUNITIES_ENDPOINTS.PHOTO_UPLOAD_URL(communityId),
        {
          filename: file.name,
          contentType: file.type,
        }
      );

      const { uploadUrl, key } = uploadUrlResponse.data;

      // Step 2: Upload to S3
      await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      // Step 3: Update community
      const updateResponse = await api.patch(
        COMMUNITIES_ENDPOINTS.PHOTO(communityId),
        {
          photoURL: `/${key}`,
        }
      );

      return updateResponse.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Delete community photo
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Delete response
   */
  deleteCommunityPhoto: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.PHOTO(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community photo (signed URL)
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} Photo data with signedUrl
   */
  getCommunityPhoto: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.PHOTO(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community rooms
   * @param {string} communityId - Community ID
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Rooms data with pagination
   */
  getCommunityRooms: async (communityId, filters = {}) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.ROOMS(communityId)}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Create community room (moderators only)
   * @param {string} communityId - Community ID
   * @param {Object} roomData - Room data
   * @param {string} roomData.name - Room name (15+ chars)
   * @param {string} roomData.description - Room description (30+ chars)
   * @param {string} roomData.field - Research field
   * @param {string} roomData.subField - Research sub-field
   * @param {string} roomData.photoURL - Photo URL
   * @returns {Promise<Object>} Created room data
   */
  createCommunityRoom: async (communityId, roomData) => {
    try {
      const api = createAuthAxios();
      const payload = {
        name: roomData.name.trim(),
        description: roomData.description.trim(),
        field: roomData.field.trim(),
        subField: roomData.subField.trim(),
        photoURL: roomData.photoURL,
      };

      const response = await api.post(
        COMMUNITIES_ENDPOINTS.ROOMS(communityId),
        payload
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get specific community room
   * @param {string} communityId - Community ID
   * @param {string} roomId - Room ID
   * @returns {Promise<Object>} Room details
   */
  getCommunityRoom: async (communityId, roomId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.ROOM_DETAIL(communityId, roomId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Update community room (room leaders or community moderators only)
   * @param {string} communityId - Community ID
   * @param {string} roomId - Room ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated room data
   */
  updateCommunityRoom: async (communityId, roomId, updateData) => {
    try {
      const api = createAuthAxios();
      const response = await api.put(
        COMMUNITIES_ENDPOINTS.ROOM_DETAIL(communityId, roomId),
        updateData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Delete community room (room leaders or community moderators only)
   * @param {string} communityId - Community ID
   * @param {string} roomId - Room ID
   * @returns {Promise<Object>} Delete response
   */
  deleteCommunityRoom: async (communityId, roomId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.ROOM_DETAIL(communityId, roomId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Request to join room
   * @param {string} communityId - Community ID
   * @param {string} roomId - Room ID
   * @returns {Promise<Object>} Join request response
   */
  requestToJoinRoom: async (communityId, roomId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.ROOM_JOIN(communityId, roomId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Handle room join request (approve/reject)
   * @param {string} communityId - Community ID
   * @param {string} roomId - Room ID
   * @param {string} userId - User ID
   * @param {string} action - 'approve' or 'reject'
   * @returns {Promise<Object>} Handle response
   */
  handleRoomJoinRequest: async (communityId, roomId, userId, action) => {
    try {
      if (!["approve", "reject"].includes(action)) {
        throw new Error('Action must be either "approve" or "reject"');
      }

      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.HANDLE_ROOM_JOIN_REQUEST(
          communityId,
          roomId,
          userId,
          action
        )
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Remove member from room
   * @param {string} communityId - Community ID
   * @param {string} roomId - Room ID
   * @param {string} memberId - Member ID
   * @returns {Promise<Object>} Remove response
   */
  removeRoomMember: async (communityId, roomId, memberId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.REMOVE_ROOM_MEMBER(communityId, roomId, memberId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Leave room
   * @param {string} communityId - Community ID
   * @param {string} roomId - Room ID
   * @returns {Promise<Object>} Leave response
   */
  leaveRoom: async (communityId, roomId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.LEAVE_ROOM(communityId, roomId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  // =================== THREADS API ===================

  /**
   * Create a new thread in community
   * @param {string} communityId - Community ID
   * @param {Object} threadData - Thread data
   * @param {string} threadData.title - Thread title (5-200 chars)
   * @param {string} threadData.description - Thread description (10-2000 chars)
   * @param {Array} threadData.tags - Tags array (max 5 tags, each max 30 chars)
   * @param {string} threadData.photoUrl - Photo URL (optional)
   * @param {string} threadData.photoKey - Photo key (optional)
   * @param {Object} threadData.flair - Flair object (optional)
   * @param {string} threadData.flair.text - Flair text
   * @param {string} threadData.flair.color - Flair text color
   * @param {string} threadData.flair.backgroundColor - Flair background color
   * @returns {Promise<Object>} Created thread data
   */
  createThread: async (communityId, threadData) => {
    try {
      const api = createAuthAxios();
      const payload = {
        title: threadData.title.trim(),
        description: threadData.description.trim(),
        tags: threadData.tags?.filter((tag) => tag.trim()) || [],
      };

      // Add photo data if provided
      if (threadData.photoUrl) {
        payload.photoUrl = threadData.photoUrl;
      }
      if (threadData.photoKey) {
        payload.photoKey = threadData.photoKey;
      }

      // Add flair if provided
      if (threadData.flair) {
        payload.flair = threadData.flair;
      }

      const response = await api.post(
        COMMUNITIES_ENDPOINTS.THREADS(communityId),
        payload
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Upload thread photo and get pre-signed URL
   * @param {string} communityId - Community ID
   * @param {File} file - Image file
   * @returns {Promise<Object>} Upload response with photoUrl and photoKey
   */
  uploadThreadPhoto: async (communityId, file) => {
    try {
      const api = createAuthAxios();

      // Validate file
      if (!file) throw new Error("No file selected");
      if (file.size > 5 * 1024 * 1024)
        throw new Error("File size must be under 5MB");

      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type))
        throw new Error("Only JPG, PNG, GIF, and WebP files are allowed");

      // Step 1: Get upload URL
      const uploadUrlResponse = await api.post(
        COMMUNITIES_ENDPOINTS.THREAD_PHOTO_UPLOAD_URL(communityId),
        {
          filename: file.name,
          contentType: file.type,
        }
      );

      const uploadData = uploadUrlResponse.data;
if (!uploadData.success) {
        throw new Error(uploadData.message || "Failed to get upload URL");
      }

      // Step 2: Upload to S3
      const uploadResponse = await fetch(uploadData.data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });
if (!uploadResponse.ok) {
        throw new Error("Failed to upload photo");
      }

      // Step 3: Get the photo URL (exact ThreadGuide implementation)
      const photoUrl = uploadData.data.uploadUrl.split("?")[0]; // Remove query params
// Test if this URL is actually accessible
fetch(photoUrl, { method: "HEAD" })
        .then((response) => {
})
        .catch((error) => {
});

      return {
        success: true,
        photoUrl,
        photoKey: uploadData.data.key,
      };
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community threads with filtering and search
   * @param {string} communityId - Community ID
   * @param {Object} filters - Filter options
   * @param {number} filters.page - Page number
   * @param {number} filters.limit - Items per page
   * @param {string} filters.sort - Sort type ('hot' | 'new' | 'top' | 'rising')
   * @param {string} filters.search - Search term
   * @param {string} filters.tags - Comma-separated tags
   * @param {string} filters.status - Thread status ('active' | 'locked' | 'archived')
   * @returns {Promise<Object>} Threads data with pagination
   */
  getCommunityThreads: async (communityId, filters = {}) => {
    try {
      // Validate communityId
      if (!communityId) {
        throw new Error("Community ID is required");
      }

      const api = createAuthAxios();

      // Prepare query parameters according to Guide 4
      const params = new URLSearchParams();

      // Add pagination parameters
      if (filters.page !== undefined && filters.page !== null) {
        params.append("page", filters.page.toString());
      }
      if (filters.limit !== undefined && filters.limit !== null) {
        params.append("limit", filters.limit.toString());
      }

      // Add sort parameter (hot, new, top, rising)
      if (filters.sort && filters.sort !== "") {
        params.append("sort", filters.sort);
      }

      // Add search parameter
      if (filters.search && filters.search.trim() !== "") {
        params.append("search", filters.search.trim());
      }

      // Add tags parameter
      if (filters.tags && filters.tags !== "") {
        params.append("tags", filters.tags);
      }

      // Add status parameter only if it's not "all"
      if (filters.status && filters.status !== "all") {
        params.append("status", filters.status);
      }

      // Add timestamp to prevent cached responses with stale view counts
      params.append("_t", Date.now().toString());

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.THREADS(communityId)}${
          params.toString() ? `?${params.toString()}` : ""
        }`,
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }
      );

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get thread details with replies
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {number} page - Page number for replies
   * @param {number} limit - Replies per page
   * @returns {Promise<Object>} Thread data with replies
   */
  getThread: async (communityId, threadId, page = 1, limit = 50) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.THREAD_DETAIL(
          communityId,
          threadId
        )}?page=${page}&limit=${limit}&_t=${Date.now()}`,
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Vote on thread (upvote/remove vote)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @returns {Promise<Object>} Vote response
   */
  voteThread: async (communityId, threadId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.VOTE_THREAD(communityId, threadId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Pin/Unpin thread (Moderators only)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @returns {Promise<Object>} Pin response
   */
  pinThread: async (communityId, threadId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.PIN_THREAD(communityId, threadId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Lock/Unlock thread (Moderators only)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @returns {Promise<Object>} Lock response
   */
  lockThread: async (communityId, threadId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.LOCK_THREAD(communityId, threadId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  // =================== REPLIES API ===================

  /**
   * Get thread replies with threading structure
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {Object} filters - Filter options
   * @param {number} filters.page - Page number
   * @param {number} filters.limit - Replies per page
   * @param {string} filters.sortBy - Sort field ('createdAt' | 'upvoteCount')
   * @param {string} filters.sortOrder - Sort order ('asc' | 'desc')
   * @returns {Promise<Object>} Threaded replies data with pagination
   */
  getThreadReplies: async (communityId, threadId, filters = {}) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();

      // Add pagination and sorting parameters
      if (filters.page !== undefined && filters.page !== null) {
        params.append("page", filters.page.toString());
      }
      if (filters.limit !== undefined && filters.limit !== null) {
        params.append("limit", filters.limit.toString());
      }
      if (filters.sortBy && filters.sortBy !== "") {
        params.append("sortBy", filters.sortBy);
      }
      if (filters.sortOrder && filters.sortOrder !== "") {
        params.append("sortOrder", filters.sortOrder);
      }

      const queryString = params.toString();
      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.REPLIES(communityId, threadId)}${
          queryString ? "?" + queryString : ""
        }`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Create a reply to thread
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {Object} replyData - Reply data
   * @param {string} replyData.content - Reply content (1-1500 chars)
   * @param {string} replyData.parentReply - Parent reply ID for nested replies (optional)
   * @returns {Promise<Object>} Created reply data
   */
  createReply: async (communityId, threadId, replyData) => {
    try {
      const api = createAuthAxios();

      // Build request body - only include parentReply if it has a valid value
      const requestBody = {
        content: replyData.content.trim(),
      };

      // Only add parentReply if it exists and is not null/undefined/empty
      if (replyData.parentReply && replyData.parentReply.trim() !== "") {
        requestBody.parentReply = replyData.parentReply.trim();
      }
const response = await api.post(
        COMMUNITIES_ENDPOINTS.REPLIES(communityId, threadId),
        requestBody
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Vote on reply (upvote/remove vote)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {string} replyId - Reply ID
   * @returns {Promise<Object>} Vote response
   */
  voteReply: async (communityId, threadId, replyId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.VOTE_REPLY(communityId, threadId, replyId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Update reply (Author only, 15 min limit)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {string} replyId - Reply ID
   * @param {string} content - Updated content
   * @returns {Promise<Object>} Updated reply data
   */
  updateReply: async (communityId, threadId, replyId, content) => {
    try {
      const api = createAuthAxios();
      const response = await api.put(
        COMMUNITIES_ENDPOINTS.REPLY_DETAIL(communityId, threadId, replyId),
        { content: content.trim() }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Delete reply
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {string} replyId - Reply ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteReply: async (communityId, threadId, replyId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.REPLY_DETAIL(communityId, threadId, replyId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  // =================== ADDITIONAL THREAD ACTIONS ===================

  /**
   * Follow/Unfollow thread (toggle)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @returns {Promise<Object>} Follow response with status
   */
  followThread: async (communityId, threadId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        `${COMMUNITIES_ENDPOINTS.THREAD_DETAIL(communityId, threadId)}/follow`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Update thread (Author or moderator only)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {Object} updateData - Update data
   * @param {string} updateData.title - Thread title (5-200 chars)
   * @param {string} updateData.description - Thread description (10-2000 chars)
   * @param {Array} updateData.tags - Tags array (max 5)
   * @returns {Promise<Object>} Updated thread data
   */
  updateThread: async (communityId, threadId, updateData) => {
    try {
      const api = createAuthAxios();
      const payload = {
        title: updateData.title?.trim(),
        description: updateData.description?.trim(),
        tags: updateData.tags?.filter((tag) => tag.trim()) || [],
      };

      const response = await api.put(
        COMMUNITIES_ENDPOINTS.THREAD_DETAIL(communityId, threadId),
        payload
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Delete thread (Author or moderator only)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteThread: async (communityId, threadId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.THREAD_DETAIL(communityId, threadId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Set thread flair
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {Object} flairData - Flair data
   * @param {string} flairData.text - Flair text
   * @param {string} flairData.color - Text color (hex)
   * @param {string} flairData.backgroundColor - Background color (hex)
   * @returns {Promise<Object>} Updated thread with flair
   */
  setThreadFlair: async (communityId, threadId, flairData) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        `${COMMUNITIES_ENDPOINTS.THREAD_DETAIL(communityId, threadId)}/flair`,
        flairData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Cross-post thread to other communities
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {Object} crosspostData - Cross-post data
   * @param {Array} crosspostData.targetCommunities - Array of community IDs
   * @returns {Promise<Object>} Cross-post result
   */
  crosspostThread: async (communityId, threadId, crosspostData) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        `${COMMUNITIES_ENDPOINTS.THREAD_DETAIL(
          communityId,
          threadId
        )}/crosspost`,
        crosspostData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Report thread
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {Object} reportData - Report data
   * @param {string} reportData.reportType - Report type (spam, harassment, inappropriate, misinformation, other)
   * @param {string} reportData.description - Required description (10-500 chars)
   * @returns {Promise<Object>} Report submission response
   */
  reportThread: async (communityId, threadId, reportData) => {
    try {
      const api = createAuthAxios();
      // Backend expects: reportType (required), description (required 10-500 chars)
      const payload = {
        reportType: reportData.reportType,  // Backend expects "reportType" NOT "reason"
        description: reportData.description?.trim() || "",
      };
const response = await api.post(
        `${COMMUNITIES_ENDPOINTS.THREAD_DETAIL(communityId, threadId)}/report`,
        payload
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Award/React to thread
   * Per AWARDS-FRONTEND-GUIDE.md:
   * POST /api/communities/:communityId/threads/:threadId/react
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {Object} awardData - Award data
   * @param {string} awardData.reactionType - Type: helpful|excellence|innovative|educational|hot_take
   * @param {string} [awardData.message] - Optional message (max 200 chars)
   * @param {boolean} [awardData.isAnonymous] - Optional: hide giver's identity
   * @returns {Promise<Object>} Award response with reaction data
   */
  awardThread: async (communityId, threadId, awardData) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        `${COMMUNITIES_ENDPOINTS.THREAD_DETAIL(communityId, threadId)}/react`,
        awardData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Remove award/reaction from thread
   * Per AWARDS-FRONTEND-GUIDE.md:
   * DELETE /api/communities/:communityId/threads/:threadId/react/:reactionType
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @param {string} reactionType - Type: helpful|excellence|innovative|educational|hot_take
   * @returns {Promise<Object>} Response confirming removal
   */
  removeThreadReaction: async (communityId, threadId, reactionType) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        `${COMMUNITIES_ENDPOINTS.THREAD_DETAIL(communityId, threadId)}/react/${reactionType}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get join requests for a community (Moderators only)
   * @param {string} communityId - Community ID
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Join requests data with pagination
   */
  getJoinRequests: async (communityId, page = 1, limit = 20) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.JOIN_REQUESTS(
          communityId
        )}?page=${page}&limit=${limit}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Handle join request (approve/reject)
   * @param {string} communityId - Community ID
   * @param {string} userId - User ID who requested to join
   * @param {string} action - Action to take ('approve' | 'reject')
   * @returns {Promise<Object>} Action result
   */
  handleJoinRequest: async (communityId, userId, action) => {
    try {
      if (!["approve", "reject"].includes(action)) {
        throw new Error('Action must be either "approve" or "reject"');
      }

      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.HANDLE_JOIN_REQUEST(communityId, userId, action)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Remove member from community
   * @param {string} communityId - Community ID
   * @param {string} memberId - Member ID to remove
   * @returns {Promise<Object>} Removal confirmation
   */
  removeMember: async (communityId, memberId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.REMOVE_MEMBER(communityId, memberId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Promote member to moderator
   * @param {string} communityId - Community ID
   * @param {string} memberId - Member ID to promote
   * @returns {Promise<Object>} Promotion confirmation
   */
  promoteMember: async (communityId, memberId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.PROMOTE_MEMBER(communityId, memberId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Demote moderator to member
   * @param {string} communityId - Community ID
   * @param {string} moderatorId - Moderator ID to demote
   * @returns {Promise<Object>} Demotion confirmation
   */
  demoteModerator: async (communityId, moderatorId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.DEMOTE_MODERATOR(communityId, moderatorId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Send a message to a community
   * @param {string} communityId - Community ID
   * @param {Object} messageData - Message data
   * @param {string} messageData.content - Message content (1-1000 chars)
   * @param {string} messageData.type - Message type ('text' | 'announcement')
   * @returns {Promise<Object>} Message data
   */
  sendMessage: async (communityId, messageData) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.MESSAGES(communityId),
        {
          content: messageData.content.trim(),
          type: messageData.type || "text",
        }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get messages for a community
   * @param {string} communityId - Community ID
   * @param {Object} options - Query options
   * @param {number} options.page - Page number
   * @param {number} options.limit - Items per page (max 100)
   * @param {string} options.before - Get messages before this ID (infinite scroll)
   * @param {string} options.type - Filter by type ('text' | 'announcement' | 'system')
   * @returns {Promise<Object>} Messages data with pagination
   */
  getMessages: async (communityId, options = {}) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();

      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.MESSAGES(communityId)}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Search messages in a community
   * @param {string} communityId - Community ID
   * @param {string} searchQuery - Search query (min 2 chars)
   * @param {Object} options - Query options
   * @param {number} options.page - Page number
   * @param {number} options.limit - Items per page (max 50)
   * @param {string} options.type - Filter by type
   * @returns {Promise<Object>} Search results
   */
  searchMessages: async (communityId, searchQuery, options = {}) => {
    try {
      if (!searchQuery || searchQuery.trim().length < 2) {
        throw new Error("Search query must be at least 2 characters long");
      }

      const api = createAuthAxios();
      const params = new URLSearchParams({
        q: searchQuery.trim(),
        ...options,
      });

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.SEARCH_MESSAGES(
          communityId
        )}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Update a message (5 min limit)
   * @param {string} communityId - Community ID
   * @param {string} messageId - Message ID
   * @param {string} content - Updated content (1-1000 chars)
   * @returns {Promise<Object>} Updated message data
   */
  updateMessage: async (communityId, messageId, content) => {
    try {
      const api = createAuthAxios();
      const response = await api.put(
        COMMUNITIES_ENDPOINTS.MESSAGE_DETAIL(communityId, messageId),
        { content: content.trim() }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Delete a message
   * @param {string} communityId - Community ID
   * @param {string} messageId - Message ID
   * @returns {Promise<Object>} Success confirmation
   */
  deleteMessage: async (communityId, messageId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.MESSAGE_DETAIL(communityId, messageId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  // =================== MODERATION API (Phase 5) ===================

  /**
   * Get moderation reports for a community
   * @param {string} communityId - Community ID
   * @param {string} status - Report status filter ('pending' | 'approved' | 'removed' | 'dismissed')
   * @returns {Promise<Object>} Reports data
   */
  getModerationReports: async (communityId, status = "pending") => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      if (status) {
        params.append("status", status);
      }
      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.MODERATION_REPORTS(
          communityId
        )}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Take action on a moderation report
   * @param {string} communityId - Community ID
   * @param {string} reportId - Report ID
   * @param {string} action - Valid actions: 'content_removed' | 'user_warned' | 'user_muted' | 'user_banned' | 'none'
   * @param {string} moderatorNotes - Notes about the action
   * @returns {Promise<Object>} Action response
   */
  actionModerationReport: async (
    communityId,
    reportId,
    action,
    moderatorNotes = ""
  ) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.MODERATION_REPORT_ACTION(communityId, reportId),
        {
          action,
          moderatorNotes,
        }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Dismiss a moderation report (no action taken)
   * @param {string} communityId - Community ID
   * @param {string} reportId - Report ID
   * @param {string} reason - Reason for dismissal
   * @returns {Promise<Object>} Dismiss response
   */
  dismissModerationReport: async (communityId, reportId, reason = "") => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        `${COMMUNITIES_ENDPOINTS.MODERATION_REPORTS(communityId)}/${reportId}/dismiss`,
        { reason }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get moderation actions log
   * @param {string} communityId - Community ID
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Actions log data
   */
  getModerationActions: async (communityId, page = 1, limit = 20) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.MODERATION_ACTIONS(
          communityId
        )}?page=${page}&limit=${limit}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Warn a user in the community
   * POST /api/communities/:communityId/moderation/users/:userId/warn
   * @param {string} communityId - Community ID
   * @param {string} userId - User ID to warn
   * @param {string} reason - Reason for warning (required)
   * @returns {Promise<Object>} Warning response with warning count
   * Note: After 3 warnings, user is automatically banned!
   */
  warnUser: async (communityId, userId, reason) => {
    try {
      if (!reason || reason.trim().length === 0) {
        throw new Error("Reason is required for issuing a warning");
      }
      const api = createAuthAxios();
      const response = await api.post(
        `/communities/${communityId}/moderation/users/${userId}/warn`,
        { reason: reason.trim() }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Mute a user in the community
   * POST /api/communities/:communityId/moderation/users/:userId/mute
   * @param {string} communityId - Community ID
   * @param {string} userId - User ID to mute
   * @param {string} reason - Reason for muting (required)
   * @param {number} duration - Duration in hours (default: 24)
   * @returns {Promise<Object>} Mute response with expiration
   */
  muteUser: async (communityId, userId, reason, duration = 24) => {
    try {
      if (!reason || reason.trim().length === 0) {
        throw new Error("Reason is required for muting a user");
      }
      const api = createAuthAxios();
      const response = await api.post(
        `/communities/${communityId}/moderation/users/${userId}/mute`,
        { reason: reason.trim(), duration }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Ban a user from the community
   * POST /api/communities/:communityId/moderation/users/:userId/ban
   * @param {string} communityId - Community ID
   * @param {string} userId - User ID to ban
   * @param {string} reason - Reason for ban (required)
   * @param {number|null} duration - Duration in hours (null = permanent)
   * @returns {Promise<Object>} Ban response with expiration
   */
  banUser: async (communityId, userId, reason, duration = 168) => {
    try {
      if (!reason || reason.trim().length === 0) {
        throw new Error("Reason is required for banning a user");
      }
      const api = createAuthAxios();
      const response = await api.post(
        `/communities/${communityId}/moderation/users/${userId}/ban`,
        { reason: reason.trim(), duration }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get banned/muted users list
   * GET /api/communities/:communityId/moderation/banned-users
   * @param {string} communityId - Community ID
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Banned users list with pagination
   */
  getBannedUsers: async (communityId, page = 1, limit = 20) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        `/communities/${communityId}/moderation/banned-users?page=${page}&limit=${limit}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Remove a restriction (unban/unmute)
   * DELETE /api/communities/:communityId/moderation/restrictions/:restrictionId
   * @param {string} communityId - Community ID
   * @param {string} restrictionId - Restriction ID to remove
   * @param {string} reason - Reason for removal (optional)
   * @returns {Promise<Object>} Removal confirmation
   */
  removeRestriction: async (communityId, restrictionId, reason = "") => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        `/communities/${communityId}/moderation/restrictions/${restrictionId}`,
        { data: { reason: reason.trim() } }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get user restrictions in a community
   * GET /api/communities/:communityId/moderation/users/:userId/restrictions
   * @param {string} communityId - Community ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User's active restrictions
   */
  getUserRestrictions: async (communityId, userId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        `/communities/${communityId}/moderation/users/${userId}/restrictions`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  // =================== ANALYTICS API (Phase 5) ===================

  /**
   * Get user's stats in a community
   * @param {string} communityId - Community ID
   * @returns {Promise<Object>} User stats data
   */
  getMyStats: async (communityId) => {
    try {
      const api = createAuthAxios();
      const response = await api.get(
        COMMUNITIES_ENDPOINTS.MY_STATS(communityId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community leaderboard
   * @param {string} communityId - Community ID
   * @param {string} period - Time period ('week' | 'month' | 'all')
   * @param {number} limit - Number of top users to fetch
   * @returns {Promise<Object>} Leaderboard data
   */
  getLeaderboard: async (communityId, period = "all", limit = 10) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("period", period);
      params.append("limit", limit.toString());
      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.LEADERBOARD(communityId)}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get global stats across all communities user is in
   * @returns {Promise<Object>} Global stats data
   */
  getGlobalStats: async () => {
    try {
      const api = createAuthAxios();
      const response = await api.get(COMMUNITIES_ENDPOINTS.GLOBAL_STATS);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community feed sidebar data (trending topics, user activity, rising communities, hot discussions)
   * @param {Object} options - Optional query parameters
   * @param {number} options.trendingLimit - Number of trending topics (default: 5)
   * @param {number} options.risingCommunitiesLimit - Number of rising communities (default: 3)
   * @param {number} options.hotDiscussionsLimit - Number of hot discussions (default: 4)
   * @returns {Promise<Object>} Sidebar widget data
   */
  getFeedSidebar: async (options = {}) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
      
      const queryString = params.toString();
      const url = queryString 
        ? `${COMMUNITIES_ENDPOINTS.FEED_SIDEBAR}?${queryString}` 
        : COMMUNITIES_ENDPOINTS.FEED_SIDEBAR;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get community-specific sidebar data (rules, contributors, topics, moderators)
   * @param {string} communityId - Community ID
   * @param {Object} options - Optional query parameters
   * @param {number} options.topContributorsLimit - Number of top contributors (default: 5)
   * @param {number} options.trendingTopicsLimit - Number of trending topics (default: 5)
   * @param {number} options.rulesLimit - Number of rules (default: 5)
   * @returns {Promise<Object>} Community sidebar data
   */
  getCommunitySpecificSidebar: async (communityId, options = {}) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
      
      const queryString = params.toString();
      const baseUrl = COMMUNITIES_ENDPOINTS.COMMUNITY_SPECIFIC_SIDEBAR(communityId);
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },
};

export default communityService;
